import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from "react";
import styles from '../styles/LandingPage.module.css';
import Header from './Header';
import odrip from '../assets/odrip_transparent.png';
import wpLiving from "../assets/WPLivingSVG.svg";
import mapImage from "../assets/townmap.png";
import flowersImage from "../assets/DSC02704a.jpg";
import shop from "../assets/W&Pa.JPG";
import dripCoffee from "../assets/DSC02496a.JPG";
import contact from "../assets/DSC02687a.JPG";

const tileMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
};

const contentMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.25, ease: 'easeOut' },
};

const LandingPage = () => {
  const [active, setActive] = useState(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [tabletHeroMenuOpen, setTabletHeroMenuOpen] = useState(false);


  useEffect(() => {
    const desktopQuery = window.matchMedia(
      "(min-width: 1024px) and (hover: hover) and (pointer: fine)"
    );

    const tabletQuery = window.matchMedia(
      "(min-width: 641px) and (max-width: 1023px), (min-width: 1024px) and (hover: none)"
    );

    const updateLayout = () => {
      setIsDesktop(desktopQuery.matches);
      setIsTablet(!desktopQuery.matches && window.innerWidth >= 641);
    };

    updateLayout();

    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener("change", updateLayout);
      tabletQuery.addEventListener("change", updateLayout);
      window.addEventListener("resize", updateLayout);
    } else {
      desktopQuery.addListener(updateLayout);
      tabletQuery.addListener(updateLayout);
      window.addEventListener("resize", updateLayout);
    }

    return () => {
      if (desktopQuery.removeEventListener) {
        desktopQuery.removeEventListener("change", updateLayout);
        tabletQuery.removeEventListener("change", updateLayout);
        window.removeEventListener("resize", updateLayout);
      } else {
        desktopQuery.removeListener(updateLayout);
        tabletQuery.removeListener(updateLayout);
        window.removeEventListener("resize", updateLayout);
      }
    };
  }, []);

  useEffect(() => {
  if (active !== "hero" || !isTablet) {
    setTabletHeroMenuOpen(false);
  }
  }, [active, isTablet]);

  const toggle = (section) => {
    setActive((prev) => (prev === section ? null : section));
  };

  const isBlurred = active !== null;

  const interactiveTileProps = (section) => {
    if (isDesktop && (section === "living" || section === "recipe" || section === "contact")) {
      return {};
    }

    return {
      role: "button",
      tabIndex: 0,
      onClick: () => toggle(section),
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle(section);
        }
      },
      "aria-expanded": active === section,
    };
  };

  return (
    <motion.main
      className={styles.page}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className={`${styles.grid} ${isBlurred ? styles.gridActive : ''}`}>
        <Header
          active={active}
          toggle={toggle}
          tabletHeroMenuOpen={tabletHeroMenuOpen}
        />

        <motion.div
          className={`${styles.panel} ${styles.address}`}
          {...tileMotion}
        >
          <img
            src={mapImage}
            alt="Holmfirth map"
            loading="lazy"
            className={styles.addressMap}
          />

          <a
            href="https://www.google.com/maps/search/?api=1&query=14D+Towngate+Holmfirth+HD9+1HA"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.panelAddress}
          >
            14D Towngate, Holmfirth, HD9 1HA
          </a>
        </motion.div>

        <motion.div
          {...interactiveTileProps("recipe")}
          className={`${styles.panel} ${styles.recipe} ${active === "recipe" ? styles.panelActive : ""}`}
          {...tileMotion}
          transition={{ duration: 0.45, delay: 0.15 }}
          whileHover={{ scale: isDesktop ? 1 : active === "recipe" ? 1 : 1.01 }}
        >
          <img
            src={dripCoffee}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className={`${styles.dripCoffee} ${isBlurred && active !== "recipe" ? styles.panelBlurred : ""}`}
          />

          <AnimatePresence mode="wait">
            {active === "recipe" ? (
              <motion.div
                key="recipe-content"
                className={styles.expandedCenterContent}
                {...contentMotion}
              >
                <p>
                  Drip is a slow coffee experience focused on clarity, ritual,
                  and beautifully brewed single origin, pour over coffees.
                </p>
                <p>
                  <a
                    href="https://www.instagram.com/oooodrip"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Instagram
                  </a>
                  {" · "}
                  <a
                    href="https://oooodrip.com/"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Website
                  </a>
                </p>
              </motion.div>
            ) : (
              <>
                <img
                  src={odrip}
                  alt="Odrip"
                  loading="lazy"
                  className={`${styles.odrip} ${isBlurred ? styles.panelBlurred : ""}`}
                />

                <div className={styles.recipeHoverContent}>
                  <p>
                    Drip is a slow coffee experience focused on clarity, ritual,
                    and beautifully brewed single origin, pour over coffees.
                  </p>
                  <p>
                    <a
                      href="https://www.instagram.com/oooodrip"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Instagram
                    </a>
                    {" · "}
                    <a
                      href="https://oooodrip.com/"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Website
                    </a>
                  </p>
                </div>
              </>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.section
          onClick={() => {
            if (isTablet) toggle("hero");
          }}
          className={`${styles.panel} ${styles.hero} ${
            active === "hero" ? styles.panelActive : ""
          } ${active === "hero" && !isTablet ? styles.heroOpen : ""}`}
          {...tileMotion}
        >
          <img
            src={flowersImage}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className={`${styles.heroImage} ${isBlurred && active !== "hero" ? styles.panelBlurred : ""}`}
          />

          {active === "hero" ? (
            isTablet ? (
              <div
                className={styles.heroTabletContent}
                onClick={(e) => e.stopPropagation()}
              >
                {!tabletHeroMenuOpen && (
                  <button
                    type="button"
                    className={styles.heroTabletClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle("hero");
                    }}
                    aria-label="Close"
                  >
                    <span className={styles.closeIcon}></span>
                  </button>
                )}

                <div className={styles.heroHoverContentTablet}>
                  {!tabletHeroMenuOpen ? (
                    <>
                      <p className={styles.heroDescription}>
                        A local neighbourhood restaurant.
                      </p>

                      <div className={styles.heroTabletActions}>
                        <motion.a
                          href="https://booking.favouritetable.com/?sitecode=2052"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.bookingButton}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Book Here
                        </motion.a>

                        <button
                          type="button"
                          className={styles.sampleMenuText}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTabletHeroMenuOpen(true);
                          }}
                        >
                          Sample menu
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={styles.heroTabletMenuTop}>
                        <button
                          type="button"
                          className={styles.sampleMenuText}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTabletHeroMenuOpen(false);
                          }}
                        >
                          Close
                        </button>

                        <motion.a
                          href="https://booking.favouritetable.com/?sitecode=2052"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.bookingButton}
                          whileHover={{ y: -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Book Here
                        </motion.a>
                      </div>

                      <motion.div
                        className={styles.heroTabletMenu}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 6 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ul
                          onClick={(e) => {
                            e.stopPropagation();
                            setTabletHeroMenuOpen(false);
                          }}
                        >
                          <li>Bread — cultured butter</li>
                          <li>Prawn — fennel, citrus</li>
                          <li>Celeriac — hazelnut, brown butter</li>
                          <li>Salmon — dill, cucumber</li>
                          <li>Beef — shallot, greens, jus</li>
                          <li>Orange — vanilla cream</li>
                          <li>Chocolate — geranium ice cream</li>
                        </ul>
                      </motion.div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.heroBottom}>
                <motion.a
                href="https://booking.favouritetable.com/?sitecode=2052"
                target="_blank"
                rel="noopener noreferrer"
                  className={styles.bookingButton}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Book Here
                </motion.a>

                <motion.div
                  className={styles.menuContent}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ul
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle("hero");
                    }}
                    className={styles.menuListClosable}
                  >
                    <li>Bread — cultured butter</li>
                    <li>Prawn — fennel, citrus</li>
                    <li>Celeriac — hazelnut, brown butter</li>
                    <li>Salmon — dill, cucumber</li>
                    <li>Beef — shallot, greens, jus</li>
                    <li>Orange — vanilla cream</li>
                    <li>Chocolate — geranium ice cream</li>
                  </ul>
                </motion.div>
              </div>
            )
          ) : (
            <>
              <motion.h1 className={styles.heroTitle}>
                The Restaurant
              </motion.h1>

              <div className={styles.heroHoverContent}>
                <p className={styles.heroDescription}>
                  A local neighbourhood restaurant.
                </p>

                <div className={styles.heroHoverActions}>
                  <motion.a
                    href="https://booking.favouritetable.com/?sitecode=2052"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.bookingButton}
                    whileHover={{ y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Book Here
                  </motion.a>

                  <button
                    type="button"
                    className={styles.sampleMenuText}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle("hero");
                    }}
                  >
                    Sample menu
                  </button>
                </div>
              </div>
            </>
          )}
        </motion.section>

        <motion.div
          {...interactiveTileProps("contact")}
          className={`${styles.panel} ${styles.contact} ${active === "contact" ? styles.panelActive : ""}`}
          {...tileMotion}
          transition={{ duration: 0.45, delay: 0.35 }}
          whileHover={{ scale: isDesktop ? 1 : active === "contact" ? 1 : 1.01 }}
        >
          <img
            src={contact}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className={`${styles.contactImgHover} ${isBlurred && active !== "contact" ? styles.panelBlurred : ""}`}
          />

          <AnimatePresence mode="wait">
            {active === "contact" ? (
              <motion.div
                key="contact-content"
                className={styles.expandedCenterContent}
                {...contentMotion}
              >
                <p>
                  <a
                    href="mailto:whippetandpickle@gmail.com"
                    onClick={(e) => e.stopPropagation()}
                  >
                    whippetandpickle@gmail.com
                  </a>
                </p>
                {/* <p>
                  <a
                    href="tel:01484443910"
                    onClick={(e) => e.stopPropagation()}
                  >
                    01484 443910
                  </a>
                </p> */}
                <a
                  href="https://www.google.com/maps/search/?api=1&query=14D+Towngate+Holmfirth+HD9+1HA"
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  14D Towngate, Holmfirth, HD9 1HA
                </a>
              </motion.div>
            ) : (
              <>
                <span className={`${styles.contactLabel} ${isBlurred ? styles.panelBlurred : ""}`}>
                  Contact
                </span>

                <div className={styles.contactHoverContent}>
                  <p>
                    <a
                      href="mailto:whippetandpickle@gmail.com"
                      onClick={(e) => e.stopPropagation()}
                    >
                      whippetandpickle@gmail.com
                    </a>
                  </p>
                  {/* <p>
                    <a
                      href="tel:01484443910"
                      onClick={(e) => e.stopPropagation()}
                    >
                      01484 443910
                    </a>
                  </p> */}
                  <p>
                    <a
                      href="https://www.google.com/maps/search/?api=1&query=14D+Towngate+Holmfirth+HD9+1HA"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      14D Towngate, Holmfirth, HD9 1HA
                    </a>
                  </p>
                </div>
              </>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div
          {...interactiveTileProps("living")}
          className={`${styles.panel} ${styles.living} ${active === "living" ? styles.panelActive : ""}`}
          {...tileMotion}
          transition={{ duration: 0.45, delay: 0.4 }}
          whileHover={{ scale: isDesktop ? 1 : active === "living" ? 1 : 1.01 }}
        >
          <img
            src={shop}
            alt=""
            loading="lazy"
            aria-hidden="true"
            className={`${styles.shop} ${isBlurred && active !== "living" ? styles.panelBlurred : ""}`}
          />

          <AnimatePresence mode="wait">
            {active === "living" ? (
              <motion.div
                key="living-content"
                className={styles.expandedCenterContent}
                {...contentMotion}
              >
                <p>
                  W&amp;P Living is a lifestyle &amp; homewares shop that brings
                  together objects, gifts, and small comforts.
                </p>
                <p>
                  <a
                    href="https://www.instagram.com/wp.living/"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Instagram
                  </a>
                  {/* {" · "}
                  <a
                    href="https://oooodrip.com/"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Shop
                  </a> */}
                </p>
              </motion.div>
            ) : (
              <>
                <img
                  src={wpLiving}
                  alt="W&P Living"
                  loading="lazy"
                  className={`${styles.wpLiving} ${isBlurred ? styles.panelBlurred : ""}`}
                />

                <div className={styles.livingHoverContent}>
                  <p>
                    W&amp;P Living is a lifestyle &amp; homewares shop that brings
                    together objects, gifts, and small comforts.
                  </p>
                  <p>
                    <a
                      href="https://www.instagram.com/wp.living/"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Instagram
                    </a>
                    {/* {" · "}
                    <a
                      href="https://oooodrip.com/"
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Shop
                    </a> */}
                  </p>
                </div>
              </>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.main>
  );
};

export default LandingPage;